import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, type MetadataSettings } from '@/lib/api';
import { log } from '@/lib/log';

// Settings store. Wraps `/api/settings`. Server is authoritative; the store
// caches the latest snapshot and exposes `update(patch)` which optimistically
// merges and PUTs back. Cascading rules (e.g. forceTranscode disables
// autoTranscode) live as computed getters so components can react cleanly.

export const useSettingsStore = defineStore('settings', () => {
    const data = ref<Record<string, unknown>>({});
    const loaded = ref(false);
    const loading = ref(false);

    async function refresh() {
        loading.value = true;
        try {
            data.value = await api.settings.get();
            loaded.value = true;
            log.debug('settings.refresh', { keys: Object.keys(data.value).length });
        } finally {
            loading.value = false;
        }
    }

    async function update(patch: Record<string, unknown>) {
        return updateWith(patch, () => api.settings.update(patch));
    }

    async function updateMetadata(next: MetadataSettings) {
        return updateWith(next, () => api.settings.updateMetadata(next));
    }

    async function updateWith(patch: Record<string, unknown>, save: () => Promise<Record<string, unknown>>) {
        const previous = data.value;
        const optimistic = { ...data.value, ...patch };
        data.value = optimistic;
        try {
            const next = await save();
            data.value = next;
            log.info('settings.update_ok', { keys: Object.keys(patch) });
        } catch (err) {
            await refresh().catch(() => {
                // Preserve the last server-confirmed snapshot if the recovery
                // request is unavailable too. Local optimistic values must not
                // survive a rejected write.
                data.value = previous;
            });
            log.warn('settings.update_failed', {
                keys: Object.keys(patch),
                error: (err as Error).message,
            });
            throw err;
        }
    }

    async function reset() {
        const next = await api.settings.reset();
        data.value = next;
        log.info('settings.reset');
    }

    function get<T = unknown>(key: string, fallback: T): T {
        const v = data.value[key];
        return v === undefined ? fallback : (v as T);
    }

    // Cascading rules ported from public/js/pages/Settings.js:
    // forceTranscode disables autoTranscode in the UI (server still accepts both
    // but the legacy app hides autoTranscode when forceTranscode is on).
    const forceTranscode = computed(() => get('forceTranscode', false));
    const autoTranscodeAvailable = computed(() => !forceTranscode.value);

    return {
        data,
        loaded,
        loading,
        refresh,
        update,
        updateMetadata,
        reset,
        get,
        forceTranscode,
        autoTranscodeAvailable,
    };
});
